import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnInit, inject, signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { UserAuthenticationService } from '../../../../../../libs/administration/src/lib/services/user-authentication.service';
import { ERP_CALLBACK_PATH, ErpSsoService } from '../erp-sso.service';

@Component({
  selector: 'app-erp-callback',
  standalone: true,
  // Styled here rather than with Bootstrap classes: navigating to /crm switches the page from the legacy
  // theme to PrimeNG while this component is still on screen.
  template: `
    <div class="callback">
      @if (errorMessage(); as message) {
        <p class="callback__error">{{ message }}</p>
        <button type="button" class="callback__button" (click)="retry()">Sign in again</button>
      } @else {
        <div class="callback__spinner" role="status" aria-label="Signing you in"></div>
        <p class="callback__text">Signing you in…</p>
      }
    </div>
  `,
  styles: `
    .callback {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      gap: 1rem;
      min-height: 100vh;
      font-family: inherit;
    }
    .callback__spinner {
      width: 2rem;
      height: 2rem;
      border: 0.25rem solid #b8945f;
      border-right-color: transparent;
      border-radius: 50%;
      animation: callback-spin 0.75s linear infinite;
    }
    .callback__text {
      margin: 0;
      color: #6c757d;
    }
    .callback__error {
      margin: 0;
      font-size: 16px;
      color: #dc3545;
    }
    .callback__button {
      padding: 0.5rem 1rem;
      border: 0;
      border-radius: 0.375rem;
      background: #b8945f;
      color: #fff;
      cursor: pointer;
    }
    @keyframes callback-spin {
      to {
        transform: rotate(360deg);
      }
    }
  `
})
export class ErpCallbackComponent implements OnInit {
  #route = inject(ActivatedRoute);
  #router = inject(Router);
  #sso = inject(ErpSsoService);
  #auth = inject(UserAuthenticationService);

  errorMessage = signal<string | null>(null);

  ngOnInit(): void {
    const params = this.#route.snapshot.queryParamMap;
    const code = params.get('code');
    const state = params.get('state');
    window.history.replaceState(null, '', ERP_CALLBACK_PATH);

    const request = code && state ? this.#sso.completeSignIn(code, state) : null;
    // Back or reload onto this page after signing in: the code is spent, but the session is fine.
    if (!request && this.#auth.isLogin()) {
      this.#router.navigateByUrl(this.#sso.takeReturnUrl());
      return;
    }
    if (!request) {
      this.errorMessage.set('This sign-in link is not valid any more.');
      return;
    }

    request.subscribe({
      next: response => {
        const { tokenDto, userInfo } = response.data;
        this.#auth.onUserLoginSuccess(userInfo, tokenDto.accessToken, tokenDto.refreshToken);
        this.#router.navigateByUrl(this.#sso.takeReturnUrl());
      },
      error: (error: HttpErrorResponse) =>
        this.errorMessage.set(
          error.status === 403
            ? 'You do not have access to the CRM. Ask an administrator to give you a CRM role in the ERP.'
            : 'We could not sign you in. Please try again.'
        )
    });
  }

  retry(): void {
    void this.#sso.startSignIn(this.#sso.takeReturnUrl());
  }
}
